import { useState } from "react";
import { motion } from "framer-motion";
import { Truck, Globe, Package, Send, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const features = [
  {
    icon: Truck,
    title: "All-India Delivery",
    description:
      "We can arrange delivery of hybrid coconut seedlings to farmers and growers across every state in India.",
  },
  {
    icon: Plane,
    title: "International Inquiries Welcome",
    description:
      "We welcome export inquiries from Malaysia, Indonesia, Sri Lanka, the Middle East and many more regions.",
  },
  {
    icon: Package,
    title: "Bulk & Wholesale Orders",
    description:
      "Special support and pricing for large-quantity, wholesale and plantation-scale orders.",
  },
  {
    icon: Globe,
    title: "Transport Assistance",
    description:
      "We help coordinate safe, healthy packing and transport arrangements based on your destination.",
  },
];

const regions = [
  "All India",
  "Malaysia",
  "Indonesia",
  "Sri Lanka",
  "Middle East",
  "& many more",
];

export default function ShippingSection() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    quantity: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.location) {
      toast.error("Please fill in your name, phone and location");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        location: formData.location,
        quantity: formData.quantity,
        message: formData.message,
        inquiry_type: "bulk-export",
      });
      toast.success(
        "Export/bulk inquiry sent! Our team will reach out with delivery details."
      );
      setFormData({
        name: "",
        phone: "",
        email: "",
        location: "",
        quantity: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send inquiry. Please try again or WhatsApp us.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="shipping"
      className="py-20 md:py-32 bg-[#F9F8F4]"
      data-testid="shipping-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-4 block">
            Shipping & Delivery
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1C18] mb-6 tracking-tight"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            We Deliver Across India & Beyond
          </h2>
          <p className="text-[#4A4F45] text-lg leading-relaxed">
            Wherever you grow, we can help you get quality hybrid coconut
            seedlings. We serve farmers across all of India and welcome export
            inquiries from around the world.
          </p>

          {/* Region badges */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {regions.map((region, index) => (
              <span
                key={region}
                className="px-4 py-2 bg-white border border-[#E5E2D9] rounded-full text-sm font-medium text-[#2F5233]"
                data-testid={`region-badge-${index}`}
              >
                {region}
              </span>
            ))}
          </div>

          {/* Transportation charges note */}
          <div
            className="mt-8 max-w-2xl mx-auto p-4 bg-[#F2CC8F]/20 rounded-2xl border border-[#F2CC8F]/40"
            data-testid="transport-charges-note"
          >
            <p className="text-[#4A4F45] text-sm">
              <strong>Please note:</strong> Transportation / delivery charges are
              to be borne by the customer and depend on the quantity and delivery
              location.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Feature cards */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="bg-white p-6 rounded-3xl border border-[#E5E2D9]"
                data-testid={`shipping-feature-${index}`}
              >
                <div className="w-12 h-12 bg-[#2F5233] rounded-2xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3
                  className="text-lg font-semibold text-[#1A1C18] mb-2"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  {feature.title}
                </h3>
                <p className="text-[#4A4F45] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </motion.div>

          {/* Bulk / Export Inquiry Form */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="lg:col-span-6"
          >
            <div className="bg-[#2F5233] p-8 md:p-10 rounded-3xl">
              <h3
                className="text-2xl font-semibold text-white mb-2"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Bulk / Wholesale / Export Inquiry
              </h3>
              <p className="text-white/70 text-sm mb-6">
                Ordering in large quantity or from outside Gujarat? Share your
                details and we'll get back with delivery options and pricing.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name *"
                    className="bg-white border-transparent rounded-xl h-12 px-4"
                    data-testid="bulk-name-input"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone / WhatsApp *"
                    className="bg-white border-transparent rounded-xl h-12 px-4"
                    data-testid="bulk-phone-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email (optional)"
                    className="bg-white border-transparent rounded-xl h-12 px-4"
                    data-testid="bulk-email-input"
                  />
                  <Input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City / State / Country *"
                    className="bg-white border-transparent rounded-xl h-12 px-4"
                    data-testid="bulk-location-input"
                  />
                </div>
                <Input
                  type="text"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="Quantity required (e.g. 500 seedlings)"
                  className="bg-white border-transparent rounded-xl h-12 px-4"
                  data-testid="bulk-quantity-input"
                />
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Any additional details (delivery timeline, etc.)"
                  rows={3}
                  className="bg-white border-transparent rounded-xl px-4 py-3 resize-none"
                  data-testid="bulk-message-input"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#E07A5F] hover:bg-[#C66A50] text-white rounded-full px-8 py-6 text-lg font-medium btn-scale disabled:opacity-50"
                  data-testid="bulk-submit-btn"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Inquiry
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
